import { useGetAccountsQuery } from "@/modules/account/services";
import { useGetCurrencyExchangeTemplateQuery } from "@/modules/template/services";
import {
  CurrencyExchangeKeys,
  TransactionTypes,
  TransferKeys
} from "@/modules/transaction/common/enum";
import type {
  ICurrencyExchangeCalculation,
  ICurrencyExchangeForm,
  ICurrencyExchangeRequest,
  IPersonalizedExchangeRate,
} from "@/modules/transaction/common/types";
import { SaveAsTemplate } from "@/modules/transaction/components/SaveAsTemplate";
import { useCurrencyExchange } from "@/modules/transaction/hooks";
import {
  useCreateCurrencyExchangeMutation,
  useGetCurrencyExchangeTransferQuery,
  useLazyGetExchangeRatePersonalizedQuery,
  useUpdateCurrencyExchangeMutation,
} from "@/modules/transaction/services";
import {
  CURRENCY,
  DATE_FORMAT,
  INITIAL_PAGE_SIZE,
  INPUT_MAX_LIMIT,
} from "@/shared/common/constants";
import { CloudflareAsset, SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import {
  CloudFlareImage,
  OwnAccountSelectOption,
  Select,
  TransferLayout
} from "@/shared/components";
import {
  clampToCurrentDate,
  cleanupAmount,
  formatDate,
  isValidAmount,
  mergeByKeys,
  omitKey
} from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Image } from "@ab.uitools/ui-library/components/Image";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  isEdit?: boolean;
  isCopy?: boolean;
  isNameUnique: boolean;
  setIsNameUnique: Dispatch<SetStateAction<boolean>>;
}

export const CurrencyExchangeForm = ({ isCopy, isEdit, isNameUnique, setIsNameUnique }: Props) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const idempotencyKey = useRef(uuid4()).current;
  const templateId = searchParams.get(SearchParamKeys.TemplateId);

  const [createCurrencyExchangeApi, { isLoading: isCreateTransferLoading }] = useCreateCurrencyExchangeMutation();
  const [updateCurrencyExchangeApi, { isLoading: isUpdateTransferLoading }] = useUpdateCurrencyExchangeMutation();
  const [getPersonalizedRate, { data: rateInfo }] = useLazyGetExchangeRatePersonalizedQuery();

  const {
    data: currencyExchangeTransfer,
    isLoading: isGetTransferLoading,
  } = useGetCurrencyExchangeTransferQuery({ id: operationId }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const {
    data: template,
    isLoading: isGetTemplateLoading,
  } = useGetCurrencyExchangeTemplateQuery({
    id: templateId as string,
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !templateId
  });

  const {
    data: accountsList = [],
    isLoading: isGetAccountsLoading,
  } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    reset,
    errors,
    values,
    setValue,
    creditAccount,
    debitAccount,
    creditAccountList,
    debitAccountList,
    setCreditAccountList,
    setDebitAccountList,
    creditAccountsFilteredList,
    debitAccountsFilteredList,
    bankRateHelperText,
  } = useCurrencyExchange({ accountsList, rateInfo });

  const [personalizedInfo, setPersonalizedInfo] = useState<IPersonalizedExchangeRate | null>(null);
  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const currencyExchangeAction = useCallback((values: ICurrencyExchangeForm) => {
    const requestData: ICurrencyExchangeRequest & { wantAmount?: string } = omitKey({
      ...values,
      amount: +cleanupAmount(`${values.amount}`),
      preferentialRate: +cleanupAmount(`${values.preferentialRate}`),
      debitAccount,
      creditAccount,
    }, CurrencyExchangeKeys.WantAmount);

    return isEdit ?
      updateCurrencyExchangeApi({
        data: requestData,
        id: `${currencyExchangeTransfer?.id}`
      }).unwrap() :
      createCurrencyExchangeApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, creditAccount, debitAccount]);

  const handleSwapCurrencies = () => {
    if (!values.creditAccount || !values.debitAccount) return;
    const creditAccountsOptions = creditAccountList;
    setCreditAccountList(debitAccountList);
    setDebitAccountList(creditAccountsOptions);

    const creditAccountNumber = values.creditAccount;
    setValue(TransferKeys.CreditAccount, values.debitAccount);
    setValue(TransferKeys.DebitAccount, creditAccountNumber);
    setPersonalizedInfo(null);
    setValue(CurrencyExchangeKeys.WantAmount, "");
  };

  const getPreferentialRate = (rate?: string | number) => {
    if (values.isRateChangeAcceptable) return rate || values.rate;
    return rate || values.preferentialRate || values.rate;
  };

  const getCalculatedAmount = ({
    amount,
    rate,
    isHaveAmount,
    rateCurrency,
  }: ICurrencyExchangeCalculation) => {
    const preferentialRate = getPreferentialRate(rate);
    const currency = rateCurrency ?? rateInfo?.currency;
    const sameCurrency = currency === (isHaveAmount ? debitAccount?.currency : creditAccount?.currency);
    return sameCurrency ?
      +cleanupAmount(amount) / +cleanupAmount(preferentialRate) :
      +cleanupAmount(amount) * +cleanupAmount(preferentialRate);
  };

  const handleCalculateWantAmount = ({
    amount,
    rate,
    rateCurrency,
  }: ICurrencyExchangeCalculation) => {
    const preferentialRate = getPreferentialRate(rate);
    if (preferentialRate) {
      const calculatedAmount = getCalculatedAmount({
        amount,
        rate: preferentialRate,
        isHaveAmount: true,
        rateCurrency,
      });
      setValue(
        CurrencyExchangeKeys.WantAmount,
        calculatedAmount ? `${calculatedAmount.toFixed(2)}` : ""
      );
    }
  };

  const handleCalculateHaveAmount = ({ amount, rate }: ICurrencyExchangeCalculation) => {
    setValue(CurrencyExchangeKeys.WantAmount, amount);
    const preferentialRate = getPreferentialRate(rate);
    if (preferentialRate) {
      const calculatedAmount = getCalculatedAmount({ amount });
      setValue(
        TransferKeys.Amount, calculatedAmount ? `${calculatedAmount.toFixed(2)}` : "",
        { shouldValidate: true }
      );
    }
  };

  const handleChangePreferentialRateAmount = (preferentialRate: string) => {
    const rate = preferentialRate || values.rate;
    handleCalculateWantAmount({ amount: values.amount, rate });
  };

  const onRateChangeAccepted = (isChecked: boolean) => {
    setValue(CurrencyExchangeKeys.IsRateChangeAcceptable, isChecked);
    const rate = isChecked || (!isChecked && !values.preferentialRate) ? values.rate : values.preferentialRate;
    handleCalculateWantAmount({ amount: values.amount, rate });
  };

  const preferentialRateHelperText = useMemo(() => {
    if (debitAccount?.currency && creditAccount?.currency && rateInfo?.currency) {
      return t("transfer.currencyExchange.as", {
        fromCurrency: rateInfo.currency,
        toCurrency: rateInfo?.currency === creditAccount.currency ?
          debitAccount.currency :
          creditAccount.currency,
      });
    }
    return "";
  }, [personalizedInfo]);

  const setDefaultValues = () => {
    const data = {
      creditAccount: currencyExchangeTransfer?.creditAccount.number,
      debitAccount: currencyExchangeTransfer?.debitAccount.number,
      transactionNumber: isEdit ?
        currencyExchangeTransfer?.transactionNumber : values.transactionNumber,
      rate: currencyExchangeTransfer?.rate,
      preferentialRate: +cleanupAmount(`${currencyExchangeTransfer?.preferentialRate}`),
      isRateChangeAcceptable: currencyExchangeTransfer?.isRateChangeAcceptable,
      tin: currencyExchangeTransfer?.tin,
      amount: currencyExchangeTransfer?.amount,
      purpose: currencyExchangeTransfer?.purpose,
      currency: currencyExchangeTransfer?.currency,
    };
    handleCalculateWantAmount({
      amount: `${data.amount}`,
      rate: data.preferentialRate,
    });
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    creditAccount: values.creditAccount,
    debitAccount: values.debitAccount,
    transactionNumber: values.transactionNumber,
    rate: values.rate,
    preferentialRate: +cleanupAmount(`${values.preferentialRate}`),
    tin: values.tin,
    isRateChangeAcceptable: values.isRateChangeAcceptable,
    amount: +cleanupAmount(values.amount),
    purpose: values.purpose,
    currency: values.currency,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if (
      values.creditAccount &&
      values.debitAccount &&
      creditAccount &&
      debitAccount
    ) {
      const creditAccountCurrency = creditAccount?.currency as string;
      const debitAccountCurrency = debitAccount?.currency as string;
      if (creditAccountCurrency === debitAccountCurrency) return;
      getPersonalizedRate({
        buyCurrency: creditAccountCurrency,
        sellCurrency: debitAccountCurrency,
      }).unwrap()
        .then((data) => {
          setPersonalizedInfo(data);
          setValue(CurrencyExchangeKeys.Rate, data.rate);
          const effectiveRate = values.isRateChangeAcceptable
            ? data.rate
            : (values.preferentialRate || data.rate);
          if (values.amount) {
            handleCalculateWantAmount({
              amount: values.amount, rate: effectiveRate, rateCurrency: data.currency
            });
          } else if (values.wantAmount) {
            handleCalculateHaveAmount({ amount: values.wantAmount, rate: effectiveRate });
          }
        });
    }
  }, [
    values.creditAccount,
    values.debitAccount,
    creditAccount?.currency,
    debitAccount?.currency,
  ]);

  useEffect(() => {
    if ((isEdit || isCopy) && !!currencyExchangeTransfer?.id) {
      setDefaultValues();
      setValue(
        TransferKeys.ScheduleDate,
        clampToCurrentDate(currencyExchangeTransfer?.scheduleDate)
      );
    }
  }, [isEdit, isCopy, currencyExchangeTransfer]);

  useEffect(() => {
    if (template) {
      const data = mergeByKeys(values, {
        ...template,
        debitAccount: template?.debitAccount?.number,
        creditAccount: template?.creditAccount?.number,
      });
      reset?.(data);
    }
  }, [template]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.ScheduleDate, formatDate(new Date(), DATE_FORMAT.YYYY_MM_DD));
    }
    if (!isEdit && !isCopy && !templateId) {
      setValue(TransferKeys.Purpose, t("transfer.currencyExchange.purpose"));
    }
  }, []);

  return (
    <TransferLayout
      title={t("transfer.currencyExchange.title")}
      page="Currency exchange"
      className="currency-exchange-form"
      isEdit={isEdit}
      withCommission={false}
      hasChanges={hasChanges}
      accountsList={accountsList}
      getTransferId={currencyExchangeAction}
      transactionType={TransactionTypes.CurrencyExchange}
      isSaveDisabled={!isNameUnique && values.isSaveAsTemplate}
      isSignDisabled={!isNameUnique && values.isSaveAsTemplate}
      isLoading={isCreateTransferLoading || isUpdateTransferLoading}
      isGetTransferLoading={isGetTransferLoading || isGetTemplateLoading}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="debitAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    isClearable
                    className="custom-select"
                    options={debitAccountsFilteredList}
                    isDisabled={isGetAccountsLoading}
                    label={t("transfer.currencyExchange.fromAccount")}
                    placeholder={t("transfer.currencyExchange.chooseFromAccount")}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="creditAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    isClearable
                    className="custom-select"
                    options={creditAccountsFilteredList}
                    isDisabled={isGetAccountsLoading}
                    label={t("transfer.currencyExchange.toAccount")}
                    placeholder={t("transfer.currencyExchange.chooseToAccount")}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              {debitAccount?.currency && creditAccount?.currency && rateInfo?.currency && (
                <div className="widget p-16 mb-40">
                  <div
                    className="flexbox align-items--center p-4 gap-4 mb-16 currency-exchange-flag--wrapper">
                    <div className="flexbox align-items--center flag-container">
                      <Image
                        className="currency-exchange-flag--image"
                        imagePath={CURRENCY[debitAccount?.currency as keyof typeof CURRENCY]?.flag}
                      />
                      <Text className="pl-8">{debitAccount?.currency}</Text>
                    </div>
                    <div
                      className="flexbox flex-direction--column align-items--center fit-available">
                      <CloudFlareImage
                        className="switch-arrows cursor-pointer"
                        onClick={handleSwapCurrencies}
                        path={CloudflareAsset.CurrencyExchangeArrows}
                      />
                      <Text type="tertiary" className="mt-4">{bankRateHelperText}</Text>
                    </div>
                    <div className="flexbox align-items--center flag-container">
                      <Image
                        className="currency-exchange-flag--image"
                        imagePath={CURRENCY[creditAccount?.currency as keyof typeof CURRENCY]?.flag}
                      />
                      <Text className="pl-8">{creditAccount?.currency}</Text>
                    </div>
                  </div>
                  <div className="currency-exchange-amount-wrapper full-width">
                    <FormField
                      name="amount"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          required
                          type="numeric"
                          allowNegative={false}
                          allowLeadingZeros={false}
                          label={t("transfer.currencyExchange.iHave")}
                          handleChange={(_, val) => handleCalculateWantAmount({ amount: val })}
                          placeholder={debitAccount?.currency === CURRENCY.AMD.label ? "0.00" : "0.0"}
                          isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                          thousandSeparator=","
                          decimalScale={debitAccount?.currency === CURRENCY.AMD.label ? 1 : 2}
                          rightIconProps={{
                            Component: CURRENCY[debitAccount?.currency as string]?.symbolIcon || "",
                          }}
                        />
                      )}
                    />
                    <FormField
                      name="wantAmount"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          required
                          type="numeric"
                          allowNegative={false}
                          allowLeadingZeros={false}
                          label={t("transfer.currencyExchange.iWant")}
                          handleChange={(_, val) => handleCalculateHaveAmount({ amount: val })}
                          placeholder={creditAccount?.currency === CURRENCY.AMD.label ? "0.00" : "0.0"}
                          thousandSeparator=","
                          decimalScale={creditAccount?.currency === CURRENCY.AMD.label ? 1 : 2}
                          rightIconProps={{
                            Component: CURRENCY[creditAccount?.currency as string]?.symbolIcon || "",
                          }}
                          isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                        />
                      )}
                    />
                  </div>
                </div>
              )}
              <FormField
                name="preferentialRate"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    disabled={values.isRateChangeAcceptable}
                    placeholder={personalizedInfo?.currency === CURRENCY.AMD.label ? "0.00" : "0.0"}
                    handleChange={(_, val) => handleChangePreferentialRateAmount(val)}
                    decimalScale={personalizedInfo?.currency === CURRENCY.AMD.label ? 1 : 2}
                    thousandSeparator=","
                    rightIconProps={{
                      Component: CURRENCY[personalizedInfo?.currency as string]?.symbolIcon || "",
                    }}
                    label={t("transfer.currencyExchange.rate")}
                    helperText={preferentialRateHelperText}
                    isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                  />
                )}
              />
              <FormField
                name="isRateChangeAcceptable"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    onClick={() => {
                      onRateChangeAccepted(!values.isRateChangeAcceptable);
                    }}
                    label={t("transfer.currencyExchange.applyBankChangingRate")}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-8"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <SaveAsTemplate
                setValue={setValue}
                setIsNameUnique={setIsNameUnique}
                isSaveAsTemplate={values.isSaveAsTemplate}
              />
            </>,
            title: t("transfer.transferDetails"),
            value: "transfer_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="transactionNumber"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("transfer.transactionNumber")}
                    placeholder={t("transfer.enterTransactionNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </TransferLayout>
  );
};
